import { useCreateModuleNotesMutation, useDeleteModuleNotesMutation, useGetModuleTypesQuery, useUpdateModuleNotesMutation } from "@app/features/other/notesApiSlice";
import { Note } from "@typedefs/modulesInterfaces";
import { useAppSelector } from "@app/hooks";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import useErrorMessage from "@utils/useErrorMessage";

export const useNotesApi = () => {
    const [updateNotes, { isLoading: isUpdating, isError: isUpdateError, error: updateError }] = useUpdateModuleNotesMutation();
    const [createNotes, { isLoading: isCreating, isError: isCreateError, error: createError }] = useCreateModuleNotesMutation();
    const [deleteNotes, { isError: isDeleteError, error: deleteError }] = useDeleteModuleNotesMutation();
    const { errorMsg: updateErrorMsg } = useErrorMessage({ isError: isUpdateError, error: updateError });
    const { errorMsg: createErrorMsg } = useErrorMessage({ isError: isCreateError, error: createError });
    const { errorMsg: deleteErrorMsg } = useErrorMessage({ isError: isDeleteError, error: deleteError });
    
    const { data: moduleTypes, isError: isModuleTypesError, error: moduleTypesError } = useGetModuleTypesQuery({ isContainer: true });
    const { errorMsg: moduleTypesErrorMsg } = useErrorMessage({ isError: isModuleTypesError, error: moduleTypesError });
    const projectModuleType = moduleTypes?.find((type) => type?.name_en?.toLowerCase() === "project")?.id;
    
    const { project } = useAppSelector(selectCurrentProject);
    const projectId = project?.id;

    const updateNote = async (noteId: number, content: string): Promise<Note | null> => {
        try {
            if (!projectId) return null;
            const res = await updateNotes({
                id: noteId,
                content,
                project_id: projectId
            }).unwrap();
            return res;
        } catch (error) {
            console.error('Failed to update note:', error);
            return null;
        }
    };

    const createNote = async (content: string): Promise<Note | null> => {
        try {
            if (!projectId || !projectModuleType) return null;
            const res = await createNotes({
                content,
                module_id: projectId,
                module_type_id: projectModuleType,
                project_id: projectId
            }).unwrap();
            return res;
        } catch (error) {
            console.error('Failed to create note:', error);
            return null;
        }
    };

    const deleteNote = async (noteId: number): Promise<boolean> => {
        try {
            if (!projectId) return false;
            await deleteNotes({ id: noteId, project_id: projectId }).unwrap();
            return true;
        } catch (error) {
            console.error('Failed to delete note:', error);
            return false;
        }
    };

    return {
        updateNote,
        createNote,
        deleteNote,
        isLoading: isUpdating || isCreating,
        errors: {
            update: updateErrorMsg,
            create: createErrorMsg,
            delete: deleteErrorMsg,
            moduleTypes: moduleTypesErrorMsg
        }
    };
}; 